import { onAuthStateChanged } from "firebase/auth";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { auth } from "../lib/firebase/auth";
import { authService } from "../services/auth/authService";
import { SUPPORT_ACCESS_DENIED_MESSAGE, assertSupportPanelAccess } from "../services/auth/supportAccess";

const AuthContext = createContext(undefined);

function normalizeError(error) {
  const code = error?.code || "";
  if (error?.message === SUPPORT_ACCESS_DENIED_MESSAGE) return SUPPORT_ACCESS_DENIED_MESSAGE;
  if (String(error?.message || "").includes("SUPPORT_AGENT_USER_IDS") ||
      String(error?.message || "").includes("SUPPORT_AGENT_UIDS")) {
    return SUPPORT_ACCESS_DENIED_MESSAGE;
  }
  if (code.includes("invalid-credential")) return "E-mail ou senha inválidos.";
  if (code.includes("invalid-email")) return "E-mail inválido.";
  if (code.includes("too-many-requests")) return "Muitas tentativas. Tente novamente mais tarde.";
  if (error?.response?.status === 404) return "Usuário autenticado, mas sem perfil no sistema.";
  return error?.message || "Não foi possível autenticar.";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setProfile(null);
        setCompanies([]);
        setLoading(false);
        return;
      }

      try {
        localStorage.setItem("firebaseUid", currentUser.uid);
        const idToken = await currentUser.getIdToken();
        localStorage.setItem("token", `Bearer ${idToken}`);

        await assertSupportPanelAccess();
        setUser(currentUser);

        const account = await authService.fetchUserProfile();
        setProfile(account);
        setCompanies(Array.isArray(account?.companies) ? account.companies : []);
      } catch (_) {
        await authService.signOut();
        setUser(null);
        setProfile(null);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = useCallback(async (email, password) => {
    await authService.signIn(email, password);
    const account = await authService.fetchUserProfile();
    setProfile(account);
    setCompanies(Array.isArray(account?.companies) ? account.companies : []);
  }, []);

  const signOut = useCallback(async () => {
    await authService.signOut();
    setProfile(null);
    setCompanies([]);
  }, []);

  const selectCompany = useCallback((company) => {
    localStorage.setItem("selectedCompany", JSON.stringify(company));
  }, []);

  const value = useMemo(
    () => ({
      user,
      profile,
      companies,
      loading,
      signIn,
      signOut,
      selectCompany,
      normalizeError,
    }),
    [user, profile, companies, loading, signIn, signOut, selectCompany]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return context;
}
